// Image compression helper for Merq Log receipts.
// Policy (locked in session): Light compression — images over 3MB are resized
// to ~1.5MB at 85% quality. Smaller images pass through untouched.
// PDFs are never compressed.

const COMPRESS_THRESHOLD_BYTES = 3 * 1024 * 1024 // 3 MB
const MAX_WIDTH = 2000 // px — plenty for receipt legibility
const QUALITY = 0.85

export async function compressImageIfNeeded(file) {
  // Only compress images. Pass PDFs and anything else through.
  if (!file.type.startsWith('image/')) return file
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    // HEIC support is patchy in <canvas>. Pass through untouched.
    return file
  }
  if (file.size < COMPRESS_THRESHOLD_BYTES) return file

  return new Promise((resolve) => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img
        if (width > MAX_WIDTH) {
          const scale = MAX_WIDTH / width
          width = MAX_WIDTH
          height = Math.round(height * scale)
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file) // fall back to original if compression failed
              return
            }
            // Preserve the original filename extension
            const newFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            })
            resolve(newFile)
          },
          'image/jpeg',
          QUALITY
        )
      }
      img.onerror = () => resolve(file)
      img.src = e.target.result
    }
    reader.onerror = () => resolve(file)
    reader.readAsDataURL(file)
  })
}

export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target.result // "data:image/jpeg;base64,..."
      const base64 = dataUrl.split(',')[1]
      resolve({ base64, dataUrl, mediaType: file.type })
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
